import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './app-tour.style.scss';
import { useTranslation } from 'react-i18next';

export type TourStep = {
  id: string;
  selector?: string;
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'absolute' | 'top-left' ;
  absolute?: { x: number; y: number };
  offset?: { x?: number; y?: number };
};

type AppTourProps = {
  steps: TourStep[];
  startIndex?: number;
  isOpen: boolean;
  onClose?: () => void;
  onStepChange?: (index: number) => void;
  closeOnOverlayClick?: boolean;
};

const getRectForSelector = (selector?: string): DOMRect | null => {
  if (!selector) return null;
  try {
    const el = document.querySelector(selector);
    if (!el) return null;
    return el.getBoundingClientRect();
  } catch (e) {
    return null;
  }
};

const calculatePosition = (
  step: TourStep,
  tooltipEl: HTMLElement | null
): { left: number; top: number } => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const defaultLeft = Math.max(16, vw / 2 - (tooltipEl?.offsetWidth || 300) / 2);
  const defaultTop = Math.max(16, vh / 2 - (tooltipEl?.offsetHeight || 120) / 2);

  if (step.position === 'absolute' && step.absolute) {
    return { left: step.absolute.x + (step.offset?.x || 0), top: step.absolute.y + (step.offset?.y || 0) };
  }

  const rect = getRectForSelector(step.selector);
  if (!rect) {
    return { left: defaultLeft, top: defaultTop };
  }

  const tooltipW = tooltipEl?.offsetWidth || 300;
  const tooltipH = tooltipEl?.offsetHeight || 120;
  const offsetX = step.offset?.x || 0;
  const offsetY = step.offset?.y || 0;

  switch (step.position) {
    case 'left':
      return { left: Math.max(8, rect.left - tooltipW - 12 + offsetX), top: rect.top + rect.height / 2 - tooltipH / 2 + offsetY };
    case 'top-left':
      return { left: Math.max(8, rect.left - tooltipW - 12 + offsetX), top: rect.top + rect.height / 2 - tooltipH / 2 + offsetY };
    case 'right':
      return { left: Math.min(window.innerWidth - tooltipW - 8, rect.right + 12 + offsetX), top: rect.top + rect.height / 2 - tooltipH / 2 + offsetY };
    case 'top':
      return { left: rect.left + rect.width / 2 - tooltipW / 2 + offsetX, top: Math.max(8, rect.top - tooltipH - 12 + offsetY) };
    case 'bottom':
    default:
      return { left: rect.left + rect.width / 2 - tooltipW / 2 + offsetX, top: Math.min(window.innerHeight - tooltipH - 8, rect.bottom + 12 + offsetY) };
  }
};

export const AppTour: React.FC<AppTourProps> = ({ steps, startIndex = 0, isOpen, onClose, onStepChange, closeOnOverlayClick = true }) => {
  const [index, setIndex] = useState(startIndex);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const { t } = useTranslation()

  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex]);

  useEffect(() => {
    if (!isOpen) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;

    const update = () => {
      const step = steps[index];
      const calculated = calculatePosition(step, tooltipRef.current);
      setPos(calculated);
      if (step.selector) {
        const el = document.querySelector(step.selector) as HTMLElement | null;
        if (el) {
          const rect = el.getBoundingClientRect();
          setHighlightRect(rect);
        } else {
          setHighlightRect(null);
        }
      } else {
        setHighlightRect(null);
      }
    };

    const tryUpdate = (attemptsLeft: number) => {
      const step = steps[index];
      if (step.selector) {
        const el = document.querySelector(step.selector);
        if (!el && attemptsLeft > 0) {
          retryTimer = setTimeout(() => tryUpdate(attemptsLeft - 1), 100);
          return;
        }
      }
      update();
    };

    // Use double rAF so the tooltip has rendered with real dimensions before calculating position
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        tryUpdate(10);
      });
    });

    // Recalculate when the tooltip changes size (e.g., content reflow on mobile)
    let resizeObserver: ResizeObserver | null = null;
    if (tooltipRef.current) {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(tooltipRef.current);
    }

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (retryTimer !== null) clearTimeout(retryTimer);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      observer.disconnect();
    };
  }, [isOpen, index, steps]);

  useEffect(() => {
    onStepChange?.(index);
  }, [index, onStepChange]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (index < steps.length - 1) setIndex(i => i + 1);
    else handleClose();
  };
  const handlePrev = () => setIndex(i => Math.max(0, i - 1));
  const handleClose = () => {
    onClose?.();
    setIndex(startIndex);
    window.scrollTo(0, 0);
  };

  const step = steps[index];

  const content = (
    <div className="app-tour-overlay" aria-hidden>
      {!highlightRect && <div className="app-tour-overlay-bg" onClick={() => closeOnOverlayClick && handleClose()} />}

      {highlightRect && (
        <>
          <div
            className="app-tour-overlay-panel"
            style={{ left: 0, top: 0, width: '100%', height: `${Math.max(0, highlightRect.top - 6)}px` }}
            onClick={() => closeOnOverlayClick && handleClose()}
          />
          <div
            className="app-tour-overlay-panel"
            style={{ left: 0, top: `${highlightRect.bottom + 6}px`, width: '100%', height: `calc(100% - ${highlightRect.bottom + 6}px)` }}
            onClick={() => closeOnOverlayClick && handleClose()}
          />
          <div
            className="app-tour-overlay-panel"
            style={{ left: 0, top: `${highlightRect.top - 6}px`, width: `${Math.max(0, highlightRect.left - 6)}px`, height: `${highlightRect.height + 12}px` }}
            onClick={() => closeOnOverlayClick && handleClose()}
          />
          <div
            className="app-tour-overlay-panel"
            style={{ left: `${highlightRect.right + 6}px`, top: `${highlightRect.top - 6}px`, width: `calc(100% - ${highlightRect.right + 6}px)`, height: `${highlightRect.height + 12}px` }}
            onClick={() => closeOnOverlayClick && handleClose()}
          />

          <div
            className="app-tour-highlight"
            style={{ left: `${highlightRect.left - 6}px`, top: `${highlightRect.top - 6}px`, width: `${highlightRect.width + 12}px`, height: `${highlightRect.height + 12}px` }}
          />
        </>
      )}
      <div
        key={index}
        ref={tooltipRef}
        className={`app-tour-tooltip app-tour-tooltip--${step.position || 'bottom'}`}
        style={{ left: `${pos.left}px`, top: `${pos.top}px` }}
      >
        <button className="app-tour-close" onClick={handleClose}>
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 1L1 15M15 15L1 1.00001"
              stroke="white"
              strokeWidth={2}
              strokeLinecap="round"
            />
          </svg>

        </button>
        <div className="app-tour-content ">{step.content}</div>
        <div className="app-tour-controls">
          <span className="app-tour-step-indicator">{index + 1} / {steps.length}</span>
          <button
            className="app-tour-btn app-tour-btn--primary"
            onClick={handleNext}
          >
            {index < steps.length - 1 ? (
              <>
                {t('NEXT')}
                <svg
                  width={7}
                  height={12}
                  viewBox="0 0 7 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginLeft: 6 }}
                >
                  <path
                    d="M1.33301 1L5.74375 5.41074C6.06919 5.73618 6.06919 6.26382 5.74375 6.58926L1.33301 11"
                    stroke="#6750B8"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                  />
                </svg>
              </>
            ) : (
              t('DONE')
            )}
          </button>

        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default AppTour;
